import { supabase } from "@/integrations/supabase/client";
import { getSignedUrlForKey } from "@/lib/evidenceAccess";
import { uploadEvidenceFile } from "@/api/uploadEvidence";

export const EVIDENCE_BUCKET = "evidence" as const;

/**
 * ACTION 2: direct browser-to-bucket uploads are removed. All uploads go
 * through the hardened `upload-evidence` Edge Function, which authorizes the
 * caller, validates the file type by magic bytes and writes the metadata row
 * transactionally.
 */
export async function uploadEvidence(
  file: File,
  opts: { questionId: number; assessmentId: string; findingId?: string; prefix?: string },
) {
  const { questionId, assessmentId, findingId } = opts;
  if (!assessmentId) throw new Error("assessmentId is required to upload evidence");

  const { evidenceId } = await uploadEvidenceFile({
    assessmentId,
    questionId,
    findingId,
    file,
  });

  return {
    evidenceId,
    fileName: file.name,
    fileType: file.type || getFileTypeFromExtension(file.name),
  };
}

function getFileTypeFromExtension(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf': return 'application/pdf';
    case 'png': return 'image/png';
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'doc':
    case 'docx': return 'application/msword';
    case 'xls':
    case 'xlsx': return 'application/vnd.ms-excel';
    default: return 'application/octet-stream';
  }
}

export async function saveEvidenceMetadata(evidenceData: {
  assessmentId: string;
  questionId: number;
  filePath: string;
  fileType: string;
  uploadedBy: string;
}) {
  // Extract file name from file path for evidence table
  const fileName = evidenceData.filePath.split('/').pop() || evidenceData.filePath;

  const { error } = await supabase.from('evidence').insert({
    assessment_id: evidenceData.assessmentId,
    question_id: evidenceData.questionId,
    file_name: fileName,
    file_url: evidenceData.filePath
  });

  if (error) throw error;
}

/**
 * ACTION 2: brokered server-side. TTL is decided by the server
 * (default 300s, clamped 60–900s); the argument is accepted for
 * back-compat but ignored.
 */
export async function getSignedUrl(path: string, _expiresInSec = 300) {
  return getSignedUrlForKey(path, "inline");
}

export async function getSignedUrls(paths: string[], _expiresInSec = 300) {
  return Promise.all(paths.map((p) => getSignedUrlForKey(p, "inline")));
}
